/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, size } from '@everlywell/leaves';
import { animated as A, useSpring, config } from 'react-spring';
import { throttle } from 'lodash-es';

import { useScrolledIntoViewOnce, useScrolledIntoView } from 'utils/hooks';
import * as S from './styles';
import { ImageType, CTA } from 'utils/types';
import { generateKey } from 'utils/helpers';
import Img, { FluidObject } from 'gatsby-image';

type ContentWithImage = {
  title: string;
  description: string;
  image: FluidObject;
  imageAlt: string;
};

export interface DigitalResultsSectionProps {
  uid?: string;
  title: string;
  headline: string;
  description?: string;
  mainCta?: CTA;
  listOfContent: ContentWithImage[];
  mobileImages: ImageType[];
  desktopImages: ImageType[];
  subHeadline?: string;
  subHeadlineDescription?: string;
  backgroundColor?: string;
}

// funtion for parallax effect in mobile
const calc = (o: any) => `translateY(${o * 0.075}px)`;

export const DigitalResultsSection: React.FC<DigitalResultsSectionProps> = ({
  uid = 'digitalResultsSection',
  title,
  headline,
  description,
  mainCta,
  mobileImages,
  desktopImages,
  listOfContent,
  subHeadline,
  subHeadlineDescription,
  backgroundColor,
}) => {
  // state for triggering animation springs
  const [titleInView, setTitleInView] = useState<boolean>(false);
  const [headlineInView, setHeadlineInView] = useState<boolean>(false);

  // Animation Refs
  const sectionRef = useRef<HTMLDivElement>(null);
  const { inView: sectionInView } = useScrolledIntoView(sectionRef);

  const titleRef = useScrolledIntoViewOnce(() => {
    setTitleInView(true);
  });

  const [{ offset }, set] = useSpring(() => ({ offset: 0 }));

  // create parallax effect for desktop images onScroll
  const handleScroll: () => void = () => {
    if (!sectionRef || !sectionRef.current) {
      return;
    }
    const {
      bottom: sectionBottom,
    } = sectionRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const offset = windowHeight - sectionBottom;
    set({ offset });
  };

  const handleScrollThrottled = throttle(handleScroll, 25);

  // attach the parallax scroll event only when this section is in view
  useEffect(() => {
    if (sectionRef && sectionInView) {
      window.addEventListener('scroll', handleScrollThrottled);
    } else {
      window.removeEventListener('scroll', handleScrollThrottled);
    }
    return () => {
      window.removeEventListener('scroll', handleScrollThrottled);
    };
  }, [sectionRef, sectionInView]);

  // triggered from scrolling into view
  const titleTransition = useSpring({
    opacity: titleInView ? 1 : 0,
    transform: titleInView ? 'translateY(0)' : `translateY(${size.xl1}px)`,
    config: config.slow,
  });

  return (
    <S.Wrapper
      ref={sectionRef}
      data-testid={`${uid}-section`}
      backgroundColor={backgroundColor}
    >
      <S.Container>
        {title ? (
          <Row center="xs">
            <Col xs={12} lg={10}>
              <A.div>
                <S.SectionTitle text={title} />
              </A.div>
            </Col>
          </Row>
        ) : null}
        {headline || description ? (
          <Row center="xs">
            <Col xs={12} lg={10}>
              <A.div>
                {headline ? (
                  <S.SectionHeadline>{headline}</S.SectionHeadline>
                ) : null}
                {description ? (
                  <S.HeadlineDescription>{description}</S.HeadlineDescription>
                ) : null}
              </A.div>
            </Col>
          </Row>
        ) : null}
        <Row center="xs">
          <S.ParallaxImagesCol xs={12} md={7}>
            <S.MainImageContainer>
              <S.MobileImageContainer>
                {mobileImages[0] ? (
                  <S.MobileAnimatedImage aria-hidden="true">
                    <Img
                      fluid={mobileImages[0].fluid}
                      alt={mobileImages[0].description}
                      style={{ maxHeight: '412px' }}
                      imgStyle={{ objectFit: 'contain', left: '-60px' }}
                    />
                  </S.MobileAnimatedImage>
                ) : null}
                {mobileImages[1] ? (
                  <S.TopImage
                    aria-hidden="true"
                    style={{ transform: offset.interpolate(calc) }}
                  >
                    <Img
                      fluid={mobileImages[1].fluid}
                      alt={mobileImages[1].description}
                    />
                  </S.TopImage>
                ) : null}
                {mobileImages[2] ? (
                  <S.BottomImage
                    aria-hidden="true"
                    style={{ transform: offset.interpolate(calc) }}
                  >
                    <Img
                      fluid={mobileImages[2].fluid}
                      alt={mobileImages[2].description}
                    />
                  </S.BottomImage>
                ) : null}
              </S.MobileImageContainer>
              <S.DesktopImageContainer>
                {desktopImages[0] ? (
                  <S.MiddleImage aria-hidden="true">
                    <Img
                      fluid={desktopImages[0].fluid}
                      alt={desktopImages[0].description}
                    />
                  </S.MiddleImage>
                ) : null}
                {desktopImages[1] ? (
                  <S.TopImage
                    aria-hidden="true"
                    style={{ transform: offset.interpolate(calc) }}
                  >
                    <Img
                      fluid={desktopImages[1].fluid}
                      alt={desktopImages[1].description}
                    />
                  </S.TopImage>
                ) : null}
                {desktopImages[2] ? (
                  <S.BottomImage
                    style={{ transform: offset.interpolate(calc) }}
                  >
                    <Img
                      fluid={desktopImages[2].fluid}
                      alt={desktopImages[2].description}
                      aria-hidden="true"
                    />
                  </S.BottomImage>
                ) : null}
              </S.DesktopImageContainer>
            </S.MainImageContainer>
          </S.ParallaxImagesCol>
          <Col xs={12} md={5}>
            <S.InfoBlockContainer ref={titleRef}>
              {listOfContent && listOfContent.length
                ? listOfContent.map(content => (
                    <S.AnimatedBlockContainer
                      key={generateKey()}
                      // @ts-ignore
                      style={titleTransition}
                    >
                      <S.InfoBlock
                        title={content.title}
                        image={
                          <Img
                            fadeIn={false}
                            fluid={content.image}
                            alt={content.imageAlt}
                            style={{ width: '100%', height: '100%' }}
                          />
                        }
                        imageAlt={content.imageAlt}
                        content={content.description}
                        size="small"
                      />
                    </S.AnimatedBlockContainer>
                  ))
                : null}
              {subHeadline && subHeadlineDescription ? (
                <S.Content>
                  <S.SubHeadline>{subHeadline}</S.SubHeadline>
                  <S.SubHeadlineDescription>
                    {subHeadlineDescription}
                  </S.SubHeadlineDescription>
                  {mainCta ? (
                    <S.CTA
                      data-testid={`${uid}-cta`}
                      href={mainCta.url}
                      appearance={
                        mainCta.appearance ? mainCta.appearance : 'primary'
                      }
                    >
                      {mainCta.text}
                    </S.CTA>
                  ) : null}
                </S.Content>
              ) : null}
            </S.InfoBlockContainer>
          </Col>
        </Row>
      </S.Container>
    </S.Wrapper>
  );
};

export default DigitalResultsSection;
